/** @file / (projects browser): header, toolbar, error alert with retry, and the filtered project grid. */
import { useState } from 'react';
import { api } from '../../api/api';
import { ImportPsdButton } from '../../import/ImportPsdButton';
import { projectsApi } from '../../api/projectsApi';
import { errorText } from '../../shell/i18n/errorText';
import { useT } from '../../shell/i18n/useT';
import { navigate } from '../../shell/router/navigate';
import { projectPath } from '../../shell/router/projectPath';
import { ShellHeader } from '../../shell/ShellHeader';
import { filterProjects, type ProjectFilter } from './filterProjects';
import { ProjectCard } from './ProjectCard';
import { ProjectsToolbar } from './ProjectsToolbar';
import { useProjects } from './useProjects';
import { useThumbnails } from './useThumbnails';

const open = (id: string): void => { navigate(projectPath(id)); };

/** Live badges arrive with E8; "New project from PSD" opens the new project when its import is done. */
export function ProjectsScreen() {
  const t = useT();
  const { items, loading, failure, busy, reload, create, trash, restore, purge } = useProjects(api, projectsApi);
  const thumbs = useThumbnails(items, projectsApi);
  const [filter, setFilter] = useState<ProjectFilter>({ query: '', sort: 'updated', trash: false });
  const shown = items === null ? [] : filterProjects(items, filter);
  return (
    <main className="shell-page">
      <ShellHeader t={t} title={t('projects.title')} />
      <ProjectsToolbar t={t} filter={filter} onFilter={setFilter} onCreate={create} busy={busy} />
      <ImportPsdButton t={t} onImported={open} taken={items?.map((p) => p.name) ?? []} />
      {failure === null ? null : (
        <div role="alert" className="shell-error">
          <p>{errorText(t, failure.thrown)}</p>
          <button type="button" onClick={reload}>{t('shell.retry')}</button>
        </div>
      )}
      {loading && items === null ? <p className="shell-splash">{t('shell.loading')}</p> : null}
      {items !== null && shown.length === 0 ? <p className="shell-splash">{t('projects.empty')}</p> : null}
      <section className="project-grid">
        {shown.map((p) => <ProjectCard key={p.id} item={p} t={t} onOpen={open} onTrash={filter.trash ? undefined : trash} onRestore={filter.trash ? restore : undefined} onPurge={filter.trash ? purge : undefined} thumbUrl={thumbs[p.id]} busy={busy} />)}
      </section>
    </main>
  );
}
